<script setup lang="ts">
import { CATEGORY_MORPHOLOGY_FETCH_ERROR } from '@/pages/journalArticle/lib/journalArticleMessages';
import LawyerCard from '@/widgets/LawyerCard/index';
import EmblaCarousel from '@/shared/ui/EmblaCarousel/index';

import { PROD_AUTO_LAWYER_CATEGORY_ID } from '@/pages/journalArticle/lib/useJournalArticleConstants';

import type { ILawyerCardInfo } from '@/entities/lawyer/@x/journal';
import { useErrorHandler } from '@/shared/lib/errors';

interface CategoryMorph {
  singular_dative?: string;
}

const props = defineProps<{
  lawyers: ILawyerCardInfo[];
  catalogLink: string;
  categoryId: number;
}>();

const { $morphService } = useNuxtApp();

const isMobile: boolean = useIsMobile();
const { handleError } = useErrorHandler();
const title: Ref<string> = ref('');

const resolveTitle = async (): Promise<void> => {
  if (props.categoryId === PROD_AUTO_LAWYER_CATEGORY_ID) {
    title.value = 'Автоюристы';
    return;
  }

  try {
    const response = await $morphService.getMorphById<CategoryMorph>(
      'activity_categories',
      props.categoryId,
      {},
    );
    const dative = response?.data?.singular_dative;
    title.value = dative ? `Юристы по ${dative}` : '';
  } catch (error) {
    handleError(error, {
      context: { action: CATEGORY_MORPHOLOGY_FETCH_ERROR },
    });
  }
};

onBeforeMount(resolveTitle);
</script>

<template>
  <div :class="['article-lawyers-section', { _mobile: isMobile }]">
    <div class="article-lawyers-section__header">
      <h3 class="article-lawyers-section__title">
        <NuxtLink v-if="title" class="article-lawyers-section__link" :to="catalogLink">
          {{ title }}
          <AppIcon name="ArrowRightJournal" class="article-lawyers-section__icon" />
        </NuxtLink>
      </h3>
    </div>
    <ul v-if="!isMobile" class="article-lawyers-section__list">
      <li
        v-for="lawyer in lawyers"
        :key="lawyer.id"
        class="article-lawyers-section__list-item"
      >
        <LawyerCard :data="lawyer" is-article is-push-to-feedbacks />
      </li>
    </ul>
    <div v-else class="article-lawyers-section__slider">
      <EmblaCarousel
        class="article-lawyers-section__slider-embla"
        :dots-count="lawyers.length"
        :options="{ loop: true, slidesToScroll: 1 }"
      >
        <div
          v-for="lawyer in lawyers"
          :key="lawyer.id"
          class="article-lawyers-section__slide"
        >
          <LawyerCard :data="lawyer" is-article is-push-to-feedbacks />
        </div>
      </EmblaCarousel>
    </div>
  </div>
</template>

<style lang="less">
.article-lawyers-section {
  .flex-column(@size-x5);

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: @size-x3;
  }

  &__list {
    display: grid;
    gap: @size-x5;
    grid-template-columns: repeat(auto-fill, minmax(31.75%, 1fr));
  }

  &__list-item {
    display: flex;
    height: 100%;
  }

  &__title {
    .h-offset-reset();
  }

  &__link {
    .color-change-hover();

    display: inline-flex;
    align-items: center;
    gap: @size-x3;
    font-size: @size-x6;
    line-height: 31px;
    font-weight: 700;
    word-break: break-word;
  }

  &__icon {
    path {
      fill: @color-persimmon;
    }
  }

  &__slide {
    flex: 0 0 100%;
    min-width: 0;
  }

  &._mobile {
    gap: 0;
  }

  &._mobile &__header {
    position: relative;
    z-index: 3;
    margin-bottom: -28px;
  }

  &._mobile &__slider {
    margin-left: -@size-x4;
    margin-right: -@size-x4;
  }

  &._mobile &__slide {
    padding-left: 44px;
    padding-right: 44px;
  }

  &._mobile &__slider .embla__container {
    padding-top: 48px;
    padding-bottom: 39px;
  }
}
</style>
